/**
 * 9.2f 全站 mobile responsive — 断点单一真值。
 *
 * 与 `tailwind.config.ts` `screens` + AntD `Grid` 对齐:Nav cutoff = lg(992)。
 * `deriveBreakpoint` 是纯函数(无 DOM),vitest 直接验边界,无需 RTL/jsdom
 * (Codex Microfix 2 锁:helper-only,真 render 留 Stage 2.8 Playwright)。
 */
import { useEffect, useState } from 'react';

export const BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  '2xl': 1536,
} as const;

export interface BreakpointState {
  isMobile: boolean; // viewport < sm (< 576)
  isTablet: boolean; // sm <= viewport < lg (576-991)
  isDesktop: boolean; // viewport >= lg (>= 992) — 保现 Sider 布局
  viewport: number;
}

/** 纯派生:viewport(px)→ 三态布尔。SSR/测试直接调用,无 window 依赖。 */
export function deriveBreakpoint(viewport: number): BreakpointState {
  return {
    isMobile: viewport < BREAKPOINTS.sm,
    isTablet: viewport >= BREAKPOINTS.sm && viewport < BREAKPOINTS.lg,
    isDesktop: viewport >= BREAKPOINTS.lg,
    viewport,
  };
}

function currentViewport(): number {
  return typeof window !== 'undefined' ? window.innerWidth : BREAKPOINTS['2xl'];
}

/** React hook:监听 resize,返回当前断点态。SSR-safe(默 desktop)。 */
export function useBreakpoint(): BreakpointState {
  const [state, setState] = useState<BreakpointState>(() =>
    deriveBreakpoint(currentViewport()),
  );

  useEffect(() => {
    function onResize() {
      setState(deriveBreakpoint(currentViewport()));
    }
    window.addEventListener('resize', onResize);
    onResize(); // 挂载即同步一次(初始 SSR default → 真实 viewport)
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return state;
}
